import { cityLocale } from "../i18n";
import { cityPath } from "../urls";
import { Application, Container } from "pixi.js";
import { cityRenderResolution, isMobileGPU } from "../core/renderQuality";
import { CityApi } from "../api/client";
import type {
  CityRuntimeResponse,
  HouseData,
  PlotData,
} from "../api/types";
import { IsoProjection } from "../core/projection";
import type { MapEditor, EditorShell } from "../editor/MapEditor";
import { EffectsLayer } from "../effects/EffectsLayer";
import { HouseManager } from "../houses/HouseManager";
import {
  CityMap,
  CITY_EDITOR_DEVELOPMENT_EXTENT,
  cityGroundExtent,
} from "../map/CityMap";
import { PlotLayer } from "../map/PlotLayer";
import { CITY_GROUND_BACKGROUND } from "../map/groundPattern";
import { WaterLayer } from "../map/WaterLayer";
import { ObjectLayer } from "../objects/ObjectLayer";
import { BILLBOARD_ASSET_ID, showBillboard } from "../ui/Billboard";
import { createCityAdvertisements } from "../ui/CityAdvertisements";
import { CityUI } from "../ui/CityUI";
import { EconomyCenter } from "../ui/EconomyCenter";
import { PlanDesigner } from "../ui/PlanDesigner";
import { StoreDesigner } from "../ui/StoreDesigner";
import { StoreDirectory } from "../ui/StoreDirectory";
import { StorefrontTour } from "../ui/StorefrontTour";

import { ResidentLayer } from "../player/ResidentLayer";
import { ResidentStudio } from "../ui/ResidentStudio";

export class CityApp {
  private advertisements: Awaited<ReturnType<typeof createCityAdvertisements>> | null = null;
  private residents:ResidentLayer|null=null;
  private waterLayer:WaterLayer|null=null;
  private residentStudio:ResidentStudio|null=null;
  private readonly app = new Application();
  private readonly world = new Container();
  private ui: CityUI | null = null;
  private tour: StorefrontTour | null = null;
  private economy: EconomyCenter | null = null;
  private directory: StoreDirectory | null = null;
  private storeDesigner: StoreDesigner | null = null;
  private planDesigner: PlanDesigner | null = null;
  private mapEditor: MapEditor | null = null;
  private houses: HouseManager | null = null;
  private plots: PlotLayer | null = null;
  private operationPending = false;
  private projection: IsoProjection | null = null;
  private editorShell: EditorShell | null = null;
  private editorMode = false;
  private readonly overlayObserver = new MutationObserver(() => this.syncSceneVisibility());
  private sceneSuspended = false;
  private dragging = false;
  private editorCapturesPointer = false;
  private multiTouchGestureActive = false;
  private suppressSelectionUntil = 0;
  private lastPointer = { x: 0, y: 0 };
  private searchQuery = "";
  private district = "all";

  constructor(
    private readonly host: HTMLElement,
    private readonly runtime: CityRuntimeResponse,
    private readonly api: CityApi,
  ) {}

  async start(editorMode = false): Promise<void> {
    this.editorMode = editorMode && this.runtime.viewer.isAdmin;
    const mobileDevice = isMobileGPU();

    let resizeTarget: HTMLElement = this.host;
    if (this.editorMode) {
      // Public visitors should not download/parse the full administrative editor.
      const { createEditorShell } = await import("../editor/MapEditor");
      this.editorShell = createEditorShell(this.host);
      resizeTarget = this.editorShell.canvasWrap;
    }

    await this.app.init({
      resizeTo: resizeTarget,
      // Physical-phone DPR must be respected, but cap GPU render target pixels.
      // MSAA remains off on touch devices to protect weaker mobile GPUs.
      antialias: !mobileDevice,
      autoDensity: true,
      background: CITY_GROUND_BACKGROUND,
      resolution: cityRenderResolution(
        resizeTarget.clientWidth || window.innerWidth,
        resizeTarget.clientHeight || window.innerHeight,
        "city",
      ),
    });

    // Inspectable on real phones without relying on the desktop device emulator.
    this.app.canvas.dataset.renderResolution = String(this.app.renderer.resolution);
    // Only throttle phones. Leave the desktop Pixi ticker at its default
    // unconstrained settings; desktop performance did not need tuning.
    if (mobileDevice) this.app.ticker.maxFPS = 30;

    if (this.editorShell) {
      this.app.canvas.className = "editor-canvas";
      this.editorShell.canvasWrap.prepend(this.app.canvas);
    } else {
      this.app.canvas.className = "city-canvas";
      this.host.replaceChildren(this.app.canvas);
    }

    this.world.sortableChildren = true;
    this.app.stage.addChild(this.world);

    const projection = new IsoProjection(
      this.runtime.map.tileWidth || 64,
      this.runtime.map.tileHeight || 32,
    );
    this.projection = projection;

    const map = new CityMap(
      this.runtime.map,
      this.runtime.plots,
      projection,
      this.editorMode,
    );
    map.zIndex = -10000;
    this.world.addChild(map);

    // Animated water textures are decorative and may be large. Allow the
    // city to become interactive before they finish decoding and uploading.
    if (this.runtime.map.water?.cells.length && this.runtime.waterLayer?.enabled) {
      const water = new WaterLayer(this.runtime.waterLayer);
      this.waterLayer = water;
      water.zIndex = -11000;
      this.world.addChild(water);
      if (this.editorMode) await water.build();
      else void water.build().catch(error => console.warn("Optional water unavailable", error));
    }

    const plots = new PlotLayer(
      this.runtime.plots,
      projection,
      Math.max(this.runtime.map.extent, CITY_EDITOR_DEVELOPMENT_EXTENT),
      this.editorMode,
    );
    plots.zIndex = -4000;
    this.plots = plots;
    this.world.addChild(plots);

    const objects = new ObjectLayer(projection, this.runtime.assets, this.runtime.advertisements);
    objects.zIndex = 40;
    const objectsReady = objects.load(this.runtime.objects ?? []);
    this.world.addChild(objects);
    if (!this.editorMode) objects.onSelect(item => {
      if(this.runtime.placeObjectIds?.includes(item.id)){this.ui?.showPlace(item.id);return;}
      if (item.assetId !== BILLBOARD_ASSET_ID) return;
      const campaign = this.runtime.advertisements?.find(ad => ad.objectId === item.id);
      if (campaign && (!campaign.endsAt || Date.parse(campaign.endsAt) > Date.now())) showBillboard(campaign, this.host);
    });
    // Decorative textures must not hold the first city frame hostage.
    // Administrative editing still needs the complete editable object set.
    if (this.editorMode) await objectsReady;

    const animationStartedAt = performance.now();
    let lastWaterTick = 0;
    this.app.ticker.add(() => {
      const now = performance.now();
      objects.updateTime(
        (now - animationStartedAt) / 1000,
        this.editorMode,
      );
      // Two clipped flow textures update at ~15 fps; idle land has no water work.
      if (!document.hidden && now - lastWaterTick >= 65) {
        map.updateWater(now / 1000);
        lastWaterTick = now;
      }
    });

    const houses = new HouseManager(
      projection,
      this.runtime.assets,
      this.runtime.fileUrls,
    );
    houses.zIndex = 100;
    const housesReady = houses.load(this.runtime.houses);
    this.world.addChild(houses);
    if (this.editorMode) await housesReady;
    else {
      // Initial geometry and navigation render immediately on every device;
      // decorations and storefronts populate progressively afterward.
      void Promise.allSettled([objectsReady, housesReady]).then(() => {
        if (!this.app.stage.destroyed && !this.sceneSuspended) this.app.render();
      });
    }
    this.houses = houses;
    let lastHouseVisibility=0;
    this.app.ticker.add(()=>{
      const now=performance.now();if(now-lastHouseVisibility<120)return;
      houses.updateViewport(this.app.screen.width,this.app.screen.height);lastHouseVisibility=now;
    });

    const effects = new EffectsLayer();
    effects.zIndex = 10000;
    this.world.addChild(effects);

    if (!this.editorMode && this.runtime.advertisements?.length) {
      // Billboard image decoding is optional, never a condition for showing
      // the terrain and interactive city controls.
      void createCityAdvertisements(projection, this.runtime.advertisements, this.host)
        .then(advertisements => {
          if (this.app.stage.destroyed) { advertisements.destroy(); return; }
          this.advertisements = advertisements;
          this.world.addChild(advertisements.layer);
        })
        .catch(error => console.warn("Optional city advertisements unavailable", error));
    }

    this.fitCamera();

    const selectHouse = (
      house: import("../houses/House").House,
      updateUrl = true,
    ) => {
      effects.showSelection(house.x, house.y);
      this.ui?.selectHouse(house.data);
      this.focusProjectedPoint(house.x, house.y);
      if (updateUrl && !this.editorMode) {
        const city = this.runtime.city?.slug ?? "";
        history.replaceState(
          null,
          "",
          `${cityPath(`store/${house.data.slug}`)}?city=${encodeURIComponent(city)}`,
        );
      }
    };

    if (!this.editorMode) {
      this.tour = new StorefrontTour(this.api);
      this.planDesigner = new PlanDesigner(this.api);
      this.storeDesigner = new StoreDesigner(
        this.api,
        this.runtime.assets,
        (house) => void this.planDesigner?.open(house),
        this.runtime.viewer.isAdmin,
        (house) => void this.residents?.changed(house),
        (house) => this.updateHouse(house),
        (visible) => {
          if (!isMobileGPU()) return;
          // The shop preview has its own GPU context. Stop city drawing while
          // the full-screen designer is active so both do not render at once.
          if (visible) this.houses?.suspendVisuals();
          else this.houses?.resumeVisuals();
          this.syncSceneVisibility();
        },
      );
      this.economy = new EconomyCenter(
        this.api,
        this.runtime,
        (plot) => void this.designPlot(plot),
        (plot) => this.purchasePlot(plot),
        (balance) => this.ui?.updateBalance(balance),
      );
      this.directory = new StoreDirectory(
        this.runtime.houses,
        (data) => {
          const house = houses.getByExternalId(data.externalId);
          if (house) selectHouse(house, true);
        },
      );

      this.residentStudio=new ResidentStudio(this.api,(house)=>this.ui?.selectHouse(house));
      this.ui = new CityUI(this.host, this.runtime, this.api, {
        onSearch: (query) => {
          this.searchQuery = query;
          houses.filter(this.searchQuery, this.district);
        },
        onDistrict: (district) => {
          this.district = district;
          houses.filter(this.searchQuery, this.district);
        },
        onZoomIn: () => this.zoom(1.15),
        onZoomOut: () => this.zoom(0.87),
        onResetCamera: () => this.fitCamera(),
        onTimeChange: (hour) => {
          const normalized = ((hour % 24) + 24) % 24;
          const lightLevel =
            normalized >= 19 || normalized < 6
              ? 1
              : normalized >= 18
                ? normalized - 18
                : normalized < 7
                  ? 7 - normalized
                  : 0;
          objects.setLightLevel(lightLevel);
          houses.setLightLevel(lightLevel);
        },
        onPurchasePlot: (plot) => void this.purchasePlot(plot),
        onPurchaseListing: (plot) => void this.purchaseListing(plot),
        onLandMarket: () => {
          plots.setMarketMode(true);
          this.ui?.closeDetail();
          this.ui?.showToast(
            "زمین‌های قابل خرید روی خود شهر مشخص شدند؛ روی زمین موردنظر کلیک کنید.",
            "info",
          );
        },
        onDesignPlot: (plot) => void this.designPlot(plot),
        onOpenHouse: (house) => void this.tour?.open(house),
        onManageHouse: (house) => void this.openStudio(house),
        onManageResident: (house) => this.residentStudio?.open(house,this.runtime.viewer.isAdmin),
        onEconomy: (tab) => void this.economy?.open(tab),
        onDirectory: () => this.directory?.open(),
      });

      houses.onSelect((house) => {
        if (this.selectionBlocked()) return;
        selectHouse(house);
      });
      plots.onSelect((plot) => {
        if (this.selectionBlocked()) return;
        plots.setSelected(plot.id);
        const p = projection.worldToScreen(plot.gridX, plot.gridY);
        effects.showSelection(p.x, p.y);
        this.ui?.selectPlot(plot);
      });

      const deepLink = location.pathname.match(/\/city\/store\/([^/]+)/);
      if (deepLink) {
        const slug = decodeURIComponent(deepLink[1]);
        const house = houses.getBySlug(slug);
        if (house) {
          selectHouse(house, false);
          if (new URLSearchParams(location.search).get("design") === "1" && house.data.canManage) void this.openStudio(house.data);
        }
      }
      const requestedPlot = Number(new URLSearchParams(location.search).get("design-plot"));
      const ownedPlot = this.runtime.plots.find(plot => plot.id === requestedPlot && plot.ownedByViewer);
      if (ownedPlot) void this.designPlot(ownedPlot);
    }

    if (!this.editorMode) {
      this.residents=new ResidentLayer(this.api,projection,this.runtime.assets,this.runtime.houses,house=>this.ui?.selectResident(house),this.runtime.viewer.isCustomer);
      this.residents.zIndex=1000;this.world.addChild(this.residents);
      // Presence polling and avatar downloads are optional background work.
      // Never wait for them before displaying the city, even on desktop.
      void Promise.allSettled([objectsReady, housesReady])
        .then(() => {
          if (!this.app.stage.destroyed) return this.residents?.start();
        })
        .catch(error => console.warn("Resident layer initialization failed", error));
      this.app.canvas.dataset.residentCount=String(this.residents.children.length);
      this.app.ticker.add(()=>this.residents?.update(Date.now()));
    }
    this.bindCamera();
    window.addEventListener("resize", this.onResize);
    if (!this.editorMode) {
      // The city canvas is completely covered by full-screen modals. Pause
      // *all* scene rendering (terrain, lots, objects, residents and shops)
      // until the final modal closes, not just the storefront ticker.
      this.overlayObserver.observe(document.body, {
        attributes: true, attributeFilter: ["class"],
      });
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      this.syncSceneVisibility();
    }

    if (this.editorMode && this.editorShell) {
      const { MapEditor } = await import("../editor/MapEditor");
      const mapEditor = new MapEditor(
        this.api,
        this.app.canvas,
        this.world,
        projection,
        this.runtime.assets,
        this.runtime.assetCategories ?? Array.from(
          new Set(this.runtime.assets.map((asset) => asset.category ?? "").filter(Boolean)),
        ),
        this.runtime.plots,
        objects,
        plots,
        houses,
        this.editorShell,
        (capture) => {
          this.editorCapturesPointer = capture;
          if (capture) this.dragging = false;
        },
        (x, y) => this.focusWorldPoint(x, y),
        (source) => map.updateSource(source),
        (plot, storefront) => this.updateSavedPlot(plot, storefront),
      );
      this.mapEditor = mapEditor;
      await mapEditor.startEditor();

      const requestedAssetId = new URLSearchParams(location.search).get("edit-asset");
      if (requestedAssetId) {
        mapEditor.openAssetDefinition(requestedAssetId);
      }
    }
    // Draw only if the scene is not covered by a full-screen editor.
    if (!this.sceneSuspended) this.app.render();
  }

  private syncSceneVisibility(): void {
    if (this.editorMode || this.app.stage.destroyed) return;
    const modalOpen = document.body.classList.contains("city-modal-open");
    const documentHidden = document.hidden;
    const shouldSuspend = modalOpen || documentHidden;
    this.app.canvas.dataset.sceneSuspended = String(shouldSuspend);
    if (shouldSuspend === this.sceneSuspended) return;
    this.sceneSuspended = shouldSuspend;
    this.world.renderable = !shouldSuspend;
    this.waterLayer?.setRunning(!shouldSuspend);
    this.residents?.setSuspended(shouldSuspend);
    if (shouldSuspend) this.app.ticker.stop();
    else {
      this.app.ticker.start();
      this.app.render();
    }
  }

  private readonly onVisibilityChange = () => this.syncSceneVisibility();

  destroy(): void {
    this.overlayObserver.disconnect();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.advertisements?.destroy();
    this.residents?.destroy();
    this.residentStudio?.destroy();
    window.removeEventListener("resize", this.onResize);
    this.mapEditor?.destroy();
    this.planDesigner?.destroy();
    this.storeDesigner?.destroy();
    this.directory?.destroy();
    this.economy?.destroy();
    this.tour?.destroy();
    this.ui?.destroy();
    this.app.destroy(true);
  }

  private async purchasePlot(plot: PlotData): Promise<void> {
    if (this.operationPending) return;
    if (!confirm(
      `خرید «${plot.name}» با قیمت ${Number(plot.price).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}؟`,
    )) return;
    this.operationPending = true;
    this.ui?.setBusy(true);
    let purchaseCompleted = false;
    try {
      const result = await this.api.purchasePlot(plot.id);
      purchaseCompleted = true;
      const canonicalPlot = this.runtime.plots.find(item => item.id === plot.id) ?? plot;
      Object.assign(canonicalPlot, {ownedByViewer: true, forSale: false, storeId: result.storefront?.externalId ?? null});
      Object.assign(plot, canonicalPlot);
      this.plots?.upsertPlot(plot);
      this.runtime.viewer.walletBalance = result.walletBalance;
      this.ui?.updateBalance(result.walletBalance);
      this.economy?.close();
      this.ui?.closeDetail();
      this.ui?.showToast(
        `زمین خریداری شد. موجودی: ${Number(result.walletBalance).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}`,
        "success",
      );
      if (result.storefront) {
        this.updateHouse(result.storefront);
        await this.openStudio(result.storefront);
      } else this.ui?.selectPlot(plot);
    } catch (error) {
      this.ui?.showToast(
        purchaseCompleted
          ? "خرید زمین ثبت شد، اما نمایش مغازه کامل نشد. صفحه را تازه‌سازی کنید تا ملک را ببینید."
          : error instanceof Error ? error.message : "خرید زمین ناموفق بود.",
        "error",
      );
    } finally {
      this.operationPending = false;
      this.ui?.setBusy(false);
    }
  }

  private updateSavedPlot(plot: PlotData, storefront?: HouseData): void {
    const index = this.runtime.plots.findIndex(item => item.id === plot.id);
    if (index < 0) this.runtime.plots.push(plot);
    else this.runtime.plots.splice(index, 1, plot);
    if (storefront) this.updateHouse(storefront);
  }

  private updateHouse(house: HouseData): void {
    const existing = this.runtime.houses.findIndex(item => item.externalId === house.externalId);
    if (existing < 0) this.runtime.houses.push(house);
    else this.runtime.houses.splice(existing, 1, house);
    this.houses?.upsert(house);
    this.houses?.filter(this.searchQuery, this.district);
    void this.residents?.changed(house);
  }

  private async openStudio(house: HouseData): Promise<void> {
    if (!house.canManage) return;
    this.economy?.close();
    this.ui?.closeDetail();
    try { await this.storeDesigner?.open(house); }
    catch (error) { this.ui?.showToast(error instanceof Error ? error.message : "باز کردن طراح ناموفق بود.", "error"); }
  }

  private async purchaseListing(plot: PlotData): Promise<void> {
    const listingId = plot.listing?.listingId;
    if (!listingId) return;
    if (!confirm(
      `خرید «${plot.name}» از مالک با مبلغ ${Number(
        plot.listing?.buyerTotal ?? 0,
      ).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}؟`,
    )) return;

    this.ui?.setBusy(true);
    try {
      const result = await this.api.purchaseListing(listingId);
      this.ui?.showToast(
        `ملک خریداری شد. موجودی: ${Number(
          result.walletBalance,
        ).toLocaleString(cityLocale())} ${this.runtime.release.currencyName}`,
        "success",
      );
      window.setTimeout(() => location.reload(), 650);
    } catch (error) {
      this.ui?.showToast(
        error instanceof Error ? error.message : "خرید ملک ناموفق بود.",
        "error",
      );
      this.ui?.setBusy(false);
    }
  }

  private async designPlot(plot: PlotData): Promise<void> {
    if (this.operationPending) return;
    if (plot.storeId) {
      const house = this.houses?.getByExternalId(plot.storeId);
      if (house) {
        await this.openStudio(house.data);
        return;
      }
      location.href = cityPath("my-shop/");
      return;
    }

    this.ui?.setBusy(true);
    this.operationPending = true;
    let created = false;
    try {
      const result = await this.api.createStorefront(plot.id);
      created = true;
      plot.storeId = result.externalId;
      const canonicalPlot = this.runtime.plots.find(item => item.id === plot.id);
      if (canonicalPlot) canonicalPlot.storeId = result.externalId;
      this.plots?.upsertPlot(plot);
      this.updateHouse(result.storefront);
      await this.openStudio(result.storefront);
    } catch (error) {
      this.ui?.showToast(
        created
          ? "مغازه روی سرور ساخته شد، اما پیش‌نمایش باز نشد. صفحه را تازه‌سازی کنید."
          : error instanceof Error ? error.message : "ساخت مغازه ناموفق بود.",
        "error",
      );
    } finally {
      this.operationPending = false;
      this.ui?.setBusy(false);
    }
  }

  private fitCamera(): void {
    if (!this.projection) return;
    const extent = this.editorMode
      ? CITY_EDITOR_DEVELOPMENT_EXTENT
      : Math.max(12, this.runtime.map.extent);
    const mapWidth = extent * 2 * this.projection.tileWidth;
    const mapHeight = extent * 2 * this.projection.tileHeight;

    if (this.editorMode) {
      const availableWidth = Math.max(320, this.app.screen.width - 24);
      const availableHeight = Math.max(260, this.app.screen.height - 24);
      const scale = Math.max(
        this.minimumZoomScale(),
        Math.min(
          1.2,
          availableWidth / mapWidth,
          availableHeight / mapHeight,
        ),
      );
      this.world.scale.set(scale);
      this.world.position.set(
        this.app.screen.width / 2,
        this.app.screen.height / 2,
      );
      this.clampCameraToGround();
      return;
    }

    const topInset = window.innerWidth <= 820 ? 116 : 124;
    const availableWidth = Math.max(320, this.app.screen.width - 36);
    const availableHeight = Math.max(
      260,
      this.app.screen.height - topInset - 24,
    );
    const fittedScale = Math.min(
      1.15,
      availableWidth / mapWidth,
      availableHeight / mapHeight,
    );
    const isMobileOrTablet = window.innerWidth <= 1024;
    // On touch-sized screens start ~75% wider than the previous fit:
    // visible span grows by 1.75x, so scale is divided by 1.75.
    const scale = isMobileOrTablet
      ? Math.max(this.minimumZoomScale(), fittedScale / 1.75)
      : Math.max(this.minimumZoomScale(), fittedScale / 1.35);
    this.world.scale.set(scale);
    this.world.position.set(
      this.app.screen.width / 2,
      topInset + availableHeight / 2,
    );
    this.clampCameraToGround();
  }

  private clampCameraToGround(): void {
    if (!this.projection) return;

    const scale = this.world.scale.x;
    const extent = cityGroundExtent(this.runtime.map.extent);
    const halfWidth = extent * this.projection.tileWidth * scale;
    const halfHeight = extent * this.projection.tileHeight * scale;
    const viewportWidth = this.app.screen.width;
    const viewportHeight = this.app.screen.height;

    const minX = viewportWidth - halfWidth;
    const maxX = halfWidth;
    const minY = viewportHeight - halfHeight;
    const maxY = halfHeight;

    this.world.position.x = minX <= maxX
      ? Math.min(maxX, Math.max(minX, this.world.position.x))
      : viewportWidth / 2;
    this.world.position.y = minY <= maxY
      ? Math.min(maxY, Math.max(minY, this.world.position.y))
      : viewportHeight / 2;
  }

  private minimumZoomScale(): number {
    // Admin map editor gets a deliberately much wider overview range:
    // 0.025 is 10x farther out than the editor's previous 0.25 floor.
    if (this.editorMode) return 0.025;

    // Public city: allow a modestly wider overview without making normal
    // navigation feel detached from the map.
    return window.innerWidth <= 1024 ? 0.10 : 0.20;
  }

  private zoom(factor: number): void {
    const next = Math.max(
      this.minimumZoomScale(),
      Math.min(2.4, this.world.scale.x * factor),
    );
    this.world.scale.set(next);
    this.clampCameraToGround();
  }

  private zoomAtScreenPoint(
    factor: number,
    screenX: number,
    screenY: number,
  ): void {
    const current = this.world.scale.x;
    const next = Math.max(
      this.minimumZoomScale(),
      Math.min(2.4, current * factor),
    );
    if (!Number.isFinite(next) || Math.abs(next - current) < 0.0001) return;

    const worldX = (screenX - this.world.position.x) / current;
    const worldY = (screenY - this.world.position.y) / current;

    this.world.scale.set(next);
    this.world.position.set(
      screenX - worldX * next,
      screenY - worldY * next,
    );
    this.clampCameraToGround();
  }

  private focusProjectedPoint(x: number, y: number): void {
    const scale = this.world.scale.x;
    this.world.position.set(
      this.app.screen.width / 2 - x * scale,
      this.app.screen.height * (this.editorMode ? 0.5 : 0.54) - y * scale,
    );
    this.clampCameraToGround();
  }

  private focusWorldPoint(x: number, y: number): void {
    if (!this.projection) return;
    const point = this.projection.worldToScreen(x, y);
    this.focusProjectedPoint(point.x, point.y);
  }

  private selectionBlocked(): boolean {
    return (
      this.multiTouchGestureActive ||
      performance.now() < this.suppressSelectionUntil
    );
  }

  private bindCamera(): void {
    const canvas = this.app.canvas;
    canvas.style.touchAction = "none";

    const touchPointers = new Map<number, { x: number; y: number }>();
    let pinchDistance = 0;
    let pinchMidpoint = { x: 0, y: 0 };

    const toScreenPoint = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      return {
        x: (clientX - rect.left) * (this.app.screen.width / width),
        y: (clientY - rect.top) * (this.app.screen.height / height),
      };
    };

    const pinchState = () => {
      const points = Array.from(touchPointers.values()).slice(0, 2);
      if (points.length < 2) return null;
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      const midpointClient = {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2,
      };
      return {
        distance: Math.max(1, Math.hypot(dx, dy)),
        midpoint: toScreenPoint(midpointClient.x, midpointClient.y),
      };
    };

    const resetPinchBaseline = () => {
      const state = pinchState();
      if (!state) {
        pinchDistance = 0;
        return;
      }
      pinchDistance = state.distance;
      pinchMidpoint = state.midpoint;
    };

    canvas.addEventListener("pointerdown", (event) => {
      if (this.editorCapturesPointer) return;

      if (event.pointerType === "touch") {
        event.preventDefault();
        touchPointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch {
          // Optional browser enhancement.
        }

        if (touchPointers.size >= 2) {
          this.multiTouchGestureActive = true;
          this.suppressSelectionUntil = performance.now() + 400;
          this.dragging = false;
          resetPinchBaseline();
          return;
        }
      }

      this.dragging = true;
      this.lastPointer = { x: event.clientX, y: event.clientY };
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
        // Optional browser enhancement.
      }
    });

    canvas.addEventListener("pointermove", (event) => {
      if (this.editorCapturesPointer) return;

      if (event.pointerType === "touch" && touchPointers.has(event.pointerId)) {
        event.preventDefault();
        touchPointers.set(event.pointerId, {
          x: event.clientX,
          y: event.clientY,
        });

        if (touchPointers.size >= 2) {
          this.multiTouchGestureActive = true;
          this.suppressSelectionUntil = performance.now() + 400;
          const state = pinchState();
          if (!state) return;

          this.dragging = false;
          if (pinchDistance > 0) {
            this.world.position.x += state.midpoint.x - pinchMidpoint.x;
            this.world.position.y += state.midpoint.y - pinchMidpoint.y;
            this.zoomAtScreenPoint(
              state.distance / pinchDistance,
              state.midpoint.x,
              state.midpoint.y,
            );
          }

          pinchDistance = state.distance;
          pinchMidpoint = state.midpoint;
          return;
        }
      }

      if (!this.dragging) return;
      const dx = event.clientX - this.lastPointer.x;
      const dy = event.clientY - this.lastPointer.y;
      this.world.position.x += dx;
      this.world.position.y += dy;
      this.clampCameraToGround();
      this.lastPointer = { x: event.clientX, y: event.clientY };
    });

    const stop = (event: PointerEvent) => {
      if (event.pointerType === "touch") {
        touchPointers.delete(event.pointerId);
        pinchDistance = 0;

        if (this.multiTouchGestureActive) {
          this.suppressSelectionUntil = performance.now() + 400;
        }

        if (touchPointers.size === 1 && !this.editorCapturesPointer) {
          const remaining = Array.from(touchPointers.values())[0];
          this.dragging = true;
          this.lastPointer = { x: remaining.x, y: remaining.y };
        } else {
          this.dragging = false;
        }
        if (touchPointers.size === 0) {
          this.multiTouchGestureActive = false;
        }
        return;
      }

      this.dragging = false;
    };

    canvas.addEventListener("pointerup", stop);
    canvas.addEventListener("pointercancel", stop);
    canvas.addEventListener("lostpointercapture", stop);
    canvas.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        this.zoom(event.deltaY < 0 ? 1.08 : 0.92);
      },
      { passive: false },
    );
  }

  private readonly onResize = () => {
    this.fitCamera();
  };
}
